import * as React from 'react';

import { cn } from '@shared/lib/utils';

import type { ButtonProps } from '../primitives/button';
import { Button } from '../primitives/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './dialog';

const actionClassName = 'h-auto min-h-11 whitespace-normal py-2 sm:order-none';

export type ConfirmDialogProps = {
  title: string;
  description?: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel?: () => void;
  trigger?: React.ReactNode;
  confirmVariant?: ButtonProps['variant'];
  cancelVariant?: ButtonProps['variant'];
  confirmButtonProps?: Omit<ButtonProps, 'variant' | 'onClick'>;
  cancelButtonProps?: Omit<ButtonProps, 'variant' | 'onClick'>;
  confirmPending?: boolean;
  closeOnConfirm?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export function ConfirmDialog({
  title,
  description,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  trigger,
  confirmVariant = 'destructive',
  cancelVariant = 'outline',
  confirmButtonProps,
  cancelButtonProps,
  confirmPending = false,
  closeOnConfirm = true,
  open,
  onOpenChange,
}: ConfirmDialogProps) {
  const confirmButton = (
    <Button
      type="button"
      variant={confirmVariant}
      loading={confirmPending}
      onClick={onConfirm}
      {...confirmButtonProps}
      className={cn(actionClassName, 'order-1', confirmButtonProps?.className)}
    >
      {confirmLabel}
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        <DialogFooter className="grid auto-rows-fr gap-2 sm:flex sm:gap-3">
          <DialogClose asChild>
            <Button
              type="button"
              variant={cancelVariant}
              onClick={onCancel}
              {...cancelButtonProps}
              className={cn(actionClassName, 'order-2', cancelButtonProps?.className)}
              disabled={confirmPending || cancelButtonProps?.disabled}
            >
              {cancelLabel}
            </Button>
          </DialogClose>
          {closeOnConfirm ? <DialogClose asChild>{confirmButton}</DialogClose> : confirmButton}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
